import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Spin } from 'antd';

import styled from 'styled-components';
import { SkillIcon, TrackIcon } from 'atoms';
import { Colors } from 'context';
import { IProfession, useProfessions } from 'core';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { PATHS } from 'router/config';

/**
 * Справочник профессий (ТЗ 0039, доводка UX).
 *
 * Владелец: «хочется рассказ про профессии, включая создать свой собственный
 * стартап». Вкладка живёт в «Моём росте», а не в карьерном центре, ровно
 * потому, что раздел открыт ВСЕМ студентам: тот, кто ещё учится, — самый
 * готовый покупатель соседнего курса, а внутри `/career` он этого экрана не
 * увидел бы вовсе.
 *
 * Каждая карточка отвечает по порядку: чем человек занимается → что для этого
 * нужно (и сколько из этого у меня уже есть) → какой НАШ курс туда ведёт.
 * Третий ответ — продающая петля; без него это энциклопедия.
 */
export const GrowthProfessions = memo(() => {
  const { t } = useTranslation();
  const { professions, loading } = useProfessions();

  if (loading && !professions.length) {
    return (
      <Center>
        <Spin />
      </Center>
    );
  }
  if (!professions.length) return <Muted>{t('growth.professions.empty')}</Muted>;

  return (
    <>
      <Hint>{t('growth.professions.hint')}</Hint>
      <List>
        {professions.map((row) => (
          <ProfessionCard key={row.code} profession={row} />
        ))}
      </List>
    </>
  );
});

const ProfessionCard = memo(({ profession }: { profession: IProfession }) => {
  const { t } = useTranslation();
  // Доля закрытых целей: кольцо рисуется одним `conic-gradient`, без
  // библиотеки графиков — ради одного индикатора её тащить незачем.
  const percent = profession.totalCount
    ? Math.round((profession.haveCount / profession.totalCount) * 100)
    : 0;

  return (
    <Card>
      <CardHead>
        <Ring percent={percent}>
          <RingHole>
            <RingValue>
              {profession.haveCount}/{profession.totalCount}
            </RingValue>
          </RingHole>
        </Ring>
        <HeadText>
          <Title>
            {/* Заголовок — ССЫЛКА на страницу профессии. Без неё маршрут
                /growth/professions/:code недостижим ниоткуда: страница есть,
                а попасть на неё можно только вручную набрав адрес. */}
            {/* Значок перед названием, а не вместо кольца: кольцо говорит о
                прогрессе, знак — о том, что это за профессия. Тот же знак
                стоит на карточке трека, поэтому одну роль студент узнаёт на
                обоих экранах. */}
            <TrackIcon icon={profession.icon} size={22} />
            <TitleLink to={PATHS.growthProfession.replace(':code', profession.code)}>
              {profession.title}
            </TitleLink>
          </Title>
          {profession.summary ? <Summary>{profession.summary}</Summary> : null}
        </HeadText>
      </CardHead>

      {profession.about ? <About>{profession.about}</About> : null}

      <Facts>
        {/* Вилку показываем ТОЛЬКО заполненную: выдуманное число — обещание
            чужих денег, а ноль читается как «платят ноль».
            🔴 Три ветки, а не две: при одной границе условие «ИЛИ» с
            подстановкой «—» печатало «Вилка: 6000–— PLN», то есть ровно то
            уродство, которого комментарий и хотел избежать. */}
        {profession.salaryFrom && profession.salaryTo ? (
          <Fact>
            {t('growth.professions.salary', {
              from: profession.salaryFrom,
              to: profession.salaryTo,
              currency: profession.salaryCurrency,
            })}
          </Fact>
        ) : profession.salaryFrom || profession.salaryTo ? (
          <Fact>
            {t(
              profession.salaryFrom
                ? 'growth.professions.salaryFrom'
                : 'growth.professions.salaryTo',
              {
                value: profession.salaryFrom ?? profession.salaryTo,
                currency: profession.salaryCurrency,
              },
            )}
          </Fact>
        ) : null}
        {/* «В нашей ленте», а не «на рынке»: доска, набитая питоновскими
            вакансиями, по построению объявит Python самой востребованной
            профессией. */}
        <Fact>{t('growth.professions.vacancies', { n: profession.liveVacancies })}</Fact>
      </Facts>

      <Block>
        <BlockTitle>{t('growth.professions.needs')}</BlockTitle>
        <Chips>
          {profession.skills.map((skill) => (
            <Chip key={skill.id} have={skill.have}>
              <SkillIcon
                slug={skill.slug}
                name={skill.name}
                category={skill.category}
                iconUrl={skill.iconUrl}
                size={18}
              />
              {skill.name}
            </Chip>
          ))}
        </Chips>
      </Block>

      {profession.courses.length ? (
        <Block>
          <BlockTitle>{t('growth.professions.ourCourses')}</BlockTitle>
          {profession.courses.map((course) => (
            <CourseRow key={course.courseId}>
              <CourseLink href={course.courseUrl} target="_blank" rel="noopener noreferrer">
                {course.courseName}
              </CourseLink>
              {course.completed ? <Muted> {t('growth.professions.courseDone')}</Muted> : null}
            </CourseRow>
          ))}
        </Block>
      ) : (
        // Честно молчим: у части профессий своего курса у нас нет, и
        // подсовывать вместо него чужой значило бы продавать не то.
        <Muted>{t('growth.professions.noCourseYet')}</Muted>
      )}

      {/* Призыв к действию внизу карточки: владелец просил «отдельные
          страницы профессий с call to action». `margin-top: auto` держит его
          у нижнего края, чтобы в сетке кнопки стояли в одну линию. */}
      <MoreLink to={PATHS.growthProfession.replace(':code', profession.code)}>
        {t('growth.professions.more')}
      </MoreLink>
    </Card>
  );
});

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const Hint = styled.p`
  margin: 0 0 16px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  border: 1px solid ${Colors.gray};
  border-radius: 12px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardHead = styled.header`
  display: flex;
  align-items: center;
  gap: 14px;
`;

/**
 * Кольцо прогресса на `conic-gradient`. Библиотеки графиков в проекте нет, и
 * ради одного индикатора её заводить не стоит — тем более на webpack 4.
 */
const Ring = styled.div<{ percent: number }>`
  width: 56px;
  height: 56px;
  flex: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: conic-gradient(
    ${Colors.main} ${({ percent }) => percent}%,
    ${Colors.bg_gray} ${({ percent }) => percent}%
  );
`;

const RingHole = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${Colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RingValue = styled.span`
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.bold })}
`;

const HeadText = styled.div`
  min-width: 0;
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const TitleLink = styled(Link)`
  color: ${Colors.black};

  &:hover {
    color: ${Colors.black};
    text-decoration: underline;
  }
`;

const MoreLink = styled(Link)`
  margin-top: auto;
  align-self: flex-start;
  padding: 8px 16px;
  border-radius: 20px;
  background: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.btn_text};
    opacity: 0.9;
  }
`;

const Summary = styled.div`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const About = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small })}
`;

const Facts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
`;

const Fact = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BlockTitle = styled.h4`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

/**
 * Уже имеющийся навык подсвечен, недостающий — приглушён. Так карточка
 * читается как «сколько мне осталось», а не как список требований.
 */
const Chip = styled.span<{ have: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: 999px;
  border: 1px solid ${({ have }) => (have ? Colors.signal_green : Colors.gray)};
  background: ${({ have }) => (have ? Colors.bg_gray : 'transparent')};
  color: ${({ have }) => (have ? Colors.black : Colors.gray_dark)};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const CourseRow = styled.div``;

const CourseLink = styled.a`
  /* Жёлтый как ЦВЕТ ТЕКСТА даёт 1.33:1 на белом при норме 4.5:1 —
     правило и причина в context/constants/colors.ts. */
  color: ${Colors.black};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Muted = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;
